import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError, apiReponseError, apiReponseErrorIncludes } from '@shared';

const RATE_LIMIT_ERROR_MESSAGE =
  'Our billing system only allows a limited number of subscription updates within a short time window. Please wait a moment and try again.';

/**
 * Extract a user-friendly error message from an ApiExError.
 * Detects Stripe rate limit errors and provides a user-intuitive message.
 * Falls back to server response message or default fallback.
 */
function getStripeErrorMessage(apiExError: ApiExError, fallback: string): string {
  // Check for Stripe rate limit error
  if (
    apiReponseErrorIncludes(apiExError, 'too frequently') ||
    apiReponseErrorIncludes(apiExError, 'rate limit')
  ) {
    return RATE_LIMIT_ERROR_MESSAGE;
  }
  // Fall back to server response message or default
  const responseMessage = apiReponseError(apiExError);
  return responseMessage || fallback;
}

export type SubscriptionState = {
  stripeSubscriptionId: string;
  subscriptionIdRef: string; // Stripe subscription ID reference
  subscriptionStatus: string;
  totalSeats: number;
  ownerAdvisorId: string | null;
  unitPricePerSeat?: number | null;
  billingInterval?: string | null;
  trialEndDateTime?: Date | null;
  nextBillingDateTime?: Date | null;
  nextInvoiceAmount?: number | null;
  nextInvoiceDate?: Date | null;
};

export type SubscriptionSeatsState = {
  targetAdvisorId: string | null;
  subscription: SubscriptionState | null;
  customer: {
    creditBalance?: number | null;
  } | null;
  seats: Array<
    SubscriptionSeat & {
      advisorEmail: string | null;
      advisorName: string | null;
    }
  >;
  isOwner: boolean;
  ownerInfo: {
    advisorName: string | null;
    advisorEmail: string | null;
    organization: string | null;
  } | null;
  currentAdvisorAssignedDateTime: Date | null;
  currentAdvisorExpiresDateTime: Date | null;

  // Pending seat invitations (invitations where the current advisor is invited)
  pendingSeatInvitations: PendingSeatInvitation[] | null;

  // Fetch operations (read-only)
  fetchStatus: 'idle' | 'loading' | 'success' | 'error';
  fetchError: string | null;

  // Mutation operations (write)
  mutationStatus: 'idle' | 'loading' | 'success' | 'error';
  mutationError: string | null;
  currentMutation:
    | 'add'
    | 'remove'
    | 'unassign'
    | 'invite'
    | 'direct_assign'
    | 'update_expiration'
    | null;

  // Invitation preview (special two-step flow)
  invitationPreviewStatus: 'idle' | 'loading' | 'success' | 'error';
  invitationPreviewError: string | null;
  invitationPreview: {
    advisorExists: boolean;
    advisorInfo?: {
      advisorId: string;
      advisorFirstName: string | null;
      advisorLastName: string | null;
      advisorEmail: string;
    };
    billingPreview: BillingImpact;
  } | null;

  // Public invitation response (for accept/decline landing page) - DEPRECATED: Use protected endpoints instead
  seatInvitationResponse: {
    status: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    message: string | null;
  };

  // Protected invitation accept response
  seatInvitationAcceptResponse: {
    status: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    message: string | null;
  };

  // Protected invitation decline response
  seatInvitationDeclineResponse: {
    status: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    message: string | null;
  };

  // Billing portal session
  billingPortal: {
    status: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    url: string | null;
  };
};

type AddSeatRequestBody = {
  advisorEmail?: string;
};

type RemoveSeatRequestBody = {
  subscriptionSeatId: string;
};

type SeatInvitationRequestBody = {
  advisorEmail: string;
  isPreview: boolean;
  subscriptionSeatId?: string;
  expiresDateTime?: string | null;
};

export type InvitationPreviewResponse = {
  advisorExists: boolean;
  advisorInfo?: {
    advisorId: string;
    advisorFirstName: string | null;
    advisorLastName: string | null;
    advisorEmail: string;
  };
  billingPreview: BillingImpact;
};

export type InvitationCreatedResponse = {
  message: string;
  subscriptionSeatId: string;
  totalSeats: number;
  invitedEmail: string;
};

type SeatInvitationResponseRequestBody = {
  subscriptionSeatId: string;
  response: 'accept' | 'decline';
};

type SeatInvitationResponseResponse = {
  message: string;
  subscriptionSeatId: string;
  status: string;
};

type UpdateSeatRequestBody = {
  subscriptionSeatId: string;
  expiresDateTime: string | null;
};

const subscriptionSeatsSlice = createSlice({
  name: 'subscriptionSeats',
  initialState: initialState.subscriptionSeats,
  reducers: {
    // Fetch seats
    fetchSubscriptionSeatsByAdvisorIdRequest(state, action: PayloadAction<string>) {
      state.targetAdvisorId = action.payload;
      state.fetchStatus = 'loading';
      state.fetchError = null;
    },
    fetchSubscriptionSeatsByAdvisorIdResponse(
      state,
      {
        payload,
      }: PayloadAction<
        Pick<
          SubscriptionSeatsState,
          | 'subscription'
          | 'customer'
          | 'seats'
          | 'isOwner'
          | 'ownerInfo'
          | 'currentAdvisorAssignedDateTime'
          | 'currentAdvisorExpiresDateTime'
          | 'pendingSeatInvitations'
        >
      >,
    ) {
      state.subscription = payload.subscription;
      state.customer = payload.customer;
      state.seats = payload.seats;
      state.isOwner = payload.isOwner;
      state.ownerInfo = payload.ownerInfo;
      state.currentAdvisorAssignedDateTime = payload.currentAdvisorAssignedDateTime;
      state.currentAdvisorExpiresDateTime = payload.currentAdvisorExpiresDateTime;
      state.pendingSeatInvitations = payload.pendingSeatInvitations;
      state.fetchStatus = 'success';
      state.fetchError = null;
    },
    fetchSubscriptionSeatsByAdvisorIdFailure(state, { payload }: PayloadAction<ApiExError>) {
      state.fetchStatus = 'error';
      state.fetchError = payload.message || 'Failed to fetch subscription seats';
    },

    // Add seat
    postSubscriptionSeatByAdvisorIdRequest(
      state,
      _action: PayloadAction<{ advisorId: string; body: AddSeatRequestBody }>,
    ) {
      state.mutationStatus = 'loading';
      state.currentMutation = 'add';
      state.mutationError = null;
    },
    postSubscriptionSeatByAdvisorIdResponse(
      state,
      _action: PayloadAction<{ message: string; subscriptionSeatId: string; totalSeats: number }>,
    ) {
      state.mutationStatus = 'success';
      state.currentMutation = null;
      state.mutationError = null;
      // Seat will be updated when we refetch
    },
    postSubscriptionSeatByAdvisorIdFailure(state, { payload }: PayloadAction<ApiExError>) {
      state.mutationStatus = 'error';
      state.mutationError = getStripeErrorMessage(payload, 'Failed to add seat');
      state.currentMutation = null;
    },

    // Remove seat
    deleteSubscriptionSeatByAdvisorIdRequest(
      state,
      _action: PayloadAction<RemoveSeatRequestBody & { advisorId: string }>,
    ) {
      state.mutationStatus = 'loading';
      state.currentMutation = 'remove';
      state.mutationError = null;
    },
    deleteSubscriptionSeatByAdvisorIdResponse(
      state,
      _action: PayloadAction<{ message: string; totalSeats: number }>,
    ) {
      state.mutationStatus = 'success';
      state.currentMutation = null;
      state.mutationError = null;
    },
    deleteSubscriptionSeatByAdvisorIdFailure(state, { payload }: PayloadAction<ApiExError>) {
      state.mutationStatus = 'error';
      state.mutationError = getStripeErrorMessage(payload, 'Failed to remove seat');
      state.currentMutation = null;
    },

    // Seat invitation (with preview)
    postSeatInvitationByAdvisorIdRequest(
      state,
      action: PayloadAction<SeatInvitationRequestBody & { advisorId: string }>,
    ) {
      if (action.payload.isPreview) {
        state.invitationPreviewStatus = 'loading';
        state.invitationPreviewError = null;
      } else {
        state.mutationStatus = 'loading';
        state.currentMutation = 'invite';
        state.mutationError = null;
      }
    },
    postSeatInvitationByAdvisorIdResponse(
      state,
      action: PayloadAction<InvitationPreviewResponse | InvitationCreatedResponse>,
    ) {
      // If this is a preview response, store it
      if ('billingPreview' in action.payload) {
        state.invitationPreviewStatus = 'success';
        state.invitationPreviewError = null;
        state.invitationPreview = action.payload as InvitationPreviewResponse;
      } else {
        // If this is an actual invitation created, clear the preview
        state.mutationStatus = 'success';
        state.currentMutation = null;
        state.mutationError = null;
        state.invitationPreview = null;
      }
    },
    postSeatInvitationByAdvisorIdFailure(
      state,
      action: PayloadAction<ApiExError & { isPreview?: boolean }>,
    ) {
      // Check if this was a preview request by looking at the current state
      if (state.invitationPreviewStatus === 'loading') {
        state.invitationPreviewStatus = 'error';
        // Preview doesn't trigger Stripe calls, so use generic error extraction
        const responseMessage = apiReponseError(action.payload);
        state.invitationPreviewError = responseMessage || 'Failed to preview invitation';
      } else {
        state.mutationStatus = 'error';
        // Invitation sends can trigger Stripe calls, check for rate limit errors
        state.mutationError = getStripeErrorMessage(action.payload, 'Failed to send invitation');
        state.currentMutation = null;
      }
      state.invitationPreview = null;
    },

    // Clear invitation preview
    clearInvitationPreview(state) {
      state.invitationPreview = null;
      state.invitationPreviewStatus = 'idle';
      state.invitationPreviewError = null;
    },

    // Public invitation response (accept/decline from email)
    postSeatInvitationResponseForPublicRequest(
      state,
      _action: PayloadAction<SeatInvitationResponseRequestBody>,
    ) {
      state.seatInvitationResponse.status = 'loading';
      state.seatInvitationResponse.error = null;
      state.seatInvitationResponse.message = null;
    },
    postSeatInvitationResponseForPublicResponse(
      state,
      { payload }: PayloadAction<SeatInvitationResponseResponse>,
    ) {
      state.seatInvitationResponse.status = 'success';
      state.seatInvitationResponse.error = null;
      state.seatInvitationResponse.message = payload.message;
    },
    postSeatInvitationResponseForPublicFailure(state, { payload }: PayloadAction<ApiExError>) {
      state.seatInvitationResponse.status = 'error';
      state.seatInvitationResponse.error =
        payload.message || 'Failed to process invitation response';
      state.seatInvitationResponse.message = null;
    },

    // Clear invitation response
    clearSeatInvitationResponse(state) {
      state.seatInvitationResponse = {
        status: 'idle',
        error: null,
        message: null,
      };
    },

    // Protected invitation accept (authenticated endpoint)
    postSeatInvitationAcceptBySubscriptionSeatIdRequest(
      state,
      _action: PayloadAction<{
        subscriptionSeatId: string;
        ipAddress: string | null;
        userAgent: string;
        metadata?: Record<string, any>;
      }>,
    ) {
      state.seatInvitationAcceptResponse.status = 'loading';
      state.seatInvitationAcceptResponse.error = null;
      state.seatInvitationAcceptResponse.message = null;
    },
    postSeatInvitationAcceptBySubscriptionSeatIdResponse(
      state,
      { payload }: PayloadAction<SeatInvitationResponseResponse>,
    ) {
      state.seatInvitationAcceptResponse.status = 'success';
      state.seatInvitationAcceptResponse.error = null;
      state.seatInvitationAcceptResponse.message = payload.message;
    },
    postSeatInvitationAcceptBySubscriptionSeatIdFailure(
      state,
      { payload }: PayloadAction<ApiExError>,
    ) {
      state.seatInvitationAcceptResponse.status = 'error';
      state.seatInvitationAcceptResponse.error = payload.message || 'Failed to accept invitation';
      state.seatInvitationAcceptResponse.message = null;
    },

    // Protected invitation decline (authenticated endpoint)
    postSeatInvitationDeclineBySubscriptionSeatIdRequest(
      state,
      _action: PayloadAction<{ subscriptionSeatId: string }>,
    ) {
      state.seatInvitationDeclineResponse.status = 'loading';
      state.seatInvitationDeclineResponse.error = null;
      state.seatInvitationDeclineResponse.message = null;
    },
    postSeatInvitationDeclineBySubscriptionSeatIdResponse(
      state,
      { payload }: PayloadAction<SeatInvitationResponseResponse>,
    ) {
      state.seatInvitationDeclineResponse.status = 'success';
      state.seatInvitationDeclineResponse.error = null;
      state.seatInvitationDeclineResponse.message = payload.message;
    },
    postSeatInvitationDeclineBySubscriptionSeatIdFailure(
      state,
      { payload }: PayloadAction<ApiExError>,
    ) {
      state.seatInvitationDeclineResponse.status = 'error';
      state.seatInvitationDeclineResponse.error = payload.message || 'Failed to decline invitation';
      state.seatInvitationDeclineResponse.message = null;
    },

    // Clear error
    clearSubscriptionSeatsError(state) {
      state.fetchError = null;
      state.mutationError = null;
      state.invitationPreviewError = null;
      state.seatInvitationResponse.error = null;
    },

    // Clear all subscription seats state
    clearSubscriptionSeats(state) {
      state.targetAdvisorId = null;
      state.subscription = null;
      state.customer = null;
      state.seats = [];
      state.isOwner = false;
      state.ownerInfo = null;
      state.currentAdvisorAssignedDateTime = null;
      state.currentAdvisorExpiresDateTime = null;
      state.pendingSeatInvitations = null;
      state.fetchStatus = 'idle';
      state.fetchError = null;
      state.mutationStatus = 'idle';
      state.mutationError = null;
      state.currentMutation = null;
      state.invitationPreviewStatus = 'idle';
      state.invitationPreviewError = null;
      state.invitationPreview = null;
      state.seatInvitationResponse = {
        status: 'idle',
        error: null,
        message: null,
      };
      state.seatInvitationAcceptResponse = {
        status: 'idle',
        error: null,
        message: null,
      };
      state.seatInvitationDeclineResponse = {
        status: 'idle',
        error: null,
        message: null,
      };
      state.billingPortal = {
        status: 'idle',
        error: null,
        url: null,
      };
    },

    // Direct assign seat (wizard only)
    postDirectAssignSeatByAdvisorIdRequest(
      state,
      _action: PayloadAction<{
        advisorId: string;
        targetAdvisorId?: string;
        advisorEmail?: string;
        expiresDateTime?: string | null;
      }>,
    ) {
      state.mutationStatus = 'loading';
      state.currentMutation = 'direct_assign';
      state.mutationError = null;
    },
    postDirectAssignSeatByAdvisorIdResponse(
      state,
      _action: PayloadAction<{ message: string; subscriptionSeatId: string; advisorId: string }>,
    ) {
      state.mutationStatus = 'success';
      state.currentMutation = null;
      state.mutationError = null;
    },
    postDirectAssignSeatByAdvisorIdFailure(state, { payload }: PayloadAction<ApiExError>) {
      state.mutationStatus = 'error';
      state.mutationError = getStripeErrorMessage(payload, 'Failed to assign seat directly');
      state.currentMutation = null;
    },

    // Billing portal session
    postStripeBillingPortalSessionUrlByAdvisorIdRequest(
      state,
      _action: PayloadAction<{ advisorId: string; stripeSubscriptionId: string }>,
    ) {
      state.billingPortal.status = 'loading';
      state.billingPortal.error = null;
      state.billingPortal.url = null;
    },
    postStripeBillingPortalSessionUrlByAdvisorIdResponse(
      state,
      action: PayloadAction<{ url: string }>,
    ) {
      state.billingPortal.status = 'success';
      state.billingPortal.error = null;
      state.billingPortal.url = action.payload.url;
    },
    postStripeBillingPortalSessionUrlByAdvisorIdFailure(
      state,
      { payload }: PayloadAction<ApiExError>,
    ) {
      state.billingPortal.status = 'error';
      state.billingPortal.error = payload.message || 'Failed to create billing portal session';
      state.billingPortal.url = null;
    },

    // Clear billing portal state
    clearBillingPortalState(state) {
      state.billingPortal = {
        status: 'idle',
        error: null,
        url: null,
      };
    },

    // Update subscription seat
    postUpdateSubscriptionSeatRequest(
      state,
      _action: PayloadAction<UpdateSeatRequestBody & { advisorId: string }>,
    ) {
      state.mutationStatus = 'loading';
      state.currentMutation = 'update_expiration';
      state.mutationError = null;
    },
    postUpdateSubscriptionSeatResponse(
      state,
      _action: PayloadAction<{ message: string; subscriptionSeatId: string }>,
    ) {
      state.mutationStatus = 'success';
      state.currentMutation = null;
      state.mutationError = null;
    },
    postUpdateSubscriptionSeatFailure(state, { payload }: PayloadAction<ApiExError>) {
      state.mutationStatus = 'error';
      state.mutationError = payload.message || 'Failed to update seat';
      state.currentMutation = null;
    },
  },
});

export const {
  fetchSubscriptionSeatsByAdvisorIdRequest,
  fetchSubscriptionSeatsByAdvisorIdResponse,
  fetchSubscriptionSeatsByAdvisorIdFailure,
  postSubscriptionSeatByAdvisorIdRequest,
  postSubscriptionSeatByAdvisorIdResponse,
  postSubscriptionSeatByAdvisorIdFailure,
  deleteSubscriptionSeatByAdvisorIdRequest,
  deleteSubscriptionSeatByAdvisorIdResponse,
  deleteSubscriptionSeatByAdvisorIdFailure,
  postSeatInvitationByAdvisorIdRequest,
  postSeatInvitationByAdvisorIdResponse,
  postSeatInvitationByAdvisorIdFailure,
  clearInvitationPreview,
  postSeatInvitationResponseForPublicRequest,
  postSeatInvitationResponseForPublicResponse,
  postSeatInvitationResponseForPublicFailure,
  postSeatInvitationAcceptBySubscriptionSeatIdRequest,
  postSeatInvitationAcceptBySubscriptionSeatIdResponse,
  postSeatInvitationAcceptBySubscriptionSeatIdFailure,
  postSeatInvitationDeclineBySubscriptionSeatIdRequest,
  postSeatInvitationDeclineBySubscriptionSeatIdResponse,
  postSeatInvitationDeclineBySubscriptionSeatIdFailure,
  clearSeatInvitationResponse,
  clearSubscriptionSeatsError,
  clearSubscriptionSeats,
  postDirectAssignSeatByAdvisorIdRequest,
  postDirectAssignSeatByAdvisorIdResponse,
  postDirectAssignSeatByAdvisorIdFailure,
  postStripeBillingPortalSessionUrlByAdvisorIdRequest,
  postStripeBillingPortalSessionUrlByAdvisorIdResponse,
  postStripeBillingPortalSessionUrlByAdvisorIdFailure,
  clearBillingPortalState,
  postUpdateSubscriptionSeatRequest,
  postUpdateSubscriptionSeatResponse,
  postUpdateSubscriptionSeatFailure,
} = subscriptionSeatsSlice.actions;

export default subscriptionSeatsSlice.reducer;
